import React, { ReactElement, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../../models/enums";
import { AddressPair } from "../../../models/AddressPair";
import { IndexerService } from "../../../services/indexer.service";
import {
  Group,
  Modal,
  Stack,
  Text,
  Loader,
  Box,
  Title,
} from "@mantine/core";
import ExportCSVButton from "../exportCSVButton/ExportCSVButton.component";
import styles from "./exportCSVModal.module.scss";
import { DatePickerInput } from "@mantine/dates";
import { TabType } from "../../addressDetailsTabs/AddressDetailsTabs.component";

const DATE_LAST_WEEK = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

export default function ExportCSVModal({
  tnxCategory,
  opened,
  onClose,
  address,
}: {
  tnxCategory: TabType | null,
  opened: boolean;
  onClose: () => void;
  address: string | undefined;
}): ReactElement | null {

  const [fromDate, setFromDate] = useState<Date>(DATE_LAST_WEEK);
  const [toDate, setToDate] = useState<Date>(new Date());

  const { data: transaction, isLoading: ethAddressLoading } = useQuery({
    queryKey: [QueryKey.ethAddress, address],
    queryFn: async (): Promise<AddressPair | null> =>
      IndexerService.getAddressPair(address!),
    enabled: !!address,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return (
    <>
      <Modal.Root opened={opened} onClose={onClose}>
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              {" "}
              <Text fw={600} fz={28} className={styles.pageTitle}>
                Export Data
              </Text>
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            {ethAddressLoading ? (
              <Loader size={50} style={{ paddingTop: "20%" }} />
            ) : transaction ? (
              <>
                <Stack gap="lg" align="flex-start">
                  <Text className={styles.wrapText}>
                    Export transactions for address {address} to CSV file.
                  </Text>
                  <Group>
                    <Group>
                      <Text>from</Text>
                      <DatePickerInput
                        value={fromDate}
                        maxDate={toDate}
                        onChange={(value) => setFromDate(value as Date)}
                      />
                    </Group>
                    <Group>
                      <Text>to</Text>
                      <DatePickerInput
                        value={toDate}
                        minDate={fromDate}
                        onChange={(value) => setToDate(value as Date)}
                      />
                    </Group>
                  </Group>
                  <Box w={"100%"} mt="sm">
                    <ExportCSVButton
                      tnxCategory={tnxCategory}
                      dateRange={{ from: fromDate!, to: toDate! }}
                      address={address!}
                      onClose={onClose}
                    />
                  </Box>
                </Stack>
              </>
            ) : (
              <Box p={"md"} m={"xl"}>
                <Text size={"1.5rem"} fw={"bold"}>
                  Error exporting data!
                </Text>
              </Box>
            )}
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
